import { getApiUrl } from '../helpers/url_helper';
import { apiCall } from '../helpers/api_service';

/**
 * Utility functions for Firebase operations
 */

/**
 * Send FCM token to backend server
 * @param {string} token - FCM token
 * @param {Object} userInfo - Optional user information
 * @returns {Promise<Object>} API response
 */
export const sendTokenToBackend = async (token, userInfo = {}) => {
  try {
    const payload = {
      fcm_token: token,
      device_info: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        ...userInfo
      },
      timestamp: new Date().toISOString()
    };

    return await apiCall(getApiUrl('notifications/save-token'), {
      method: 'POST',
      body: JSON.stringify(payload),
      includeAuth: true,
    });
  } catch (error) {
    console.error('Error sending FCM token to backend:', error);
    throw error;
  }
};

/**
 * Remove FCM token from backend server
 * @param {string} token - FCM token to remove
 * @returns {Promise<Object>} API response
 */
export const removeTokenFromBackend = async (token) => {
  try {
    return await apiCall(getApiUrl('notifications/remove-token'), {
      method: 'DELETE',
      body: JSON.stringify({ fcm_token: token }),
      includeAuth: true,
    });
  } catch (error) {
    console.error('Error removing FCM token from backend:', error);
    throw error;
  }
};

/**
 * Get device information for FCM registration
 * @returns {Object} Device information
 */
export const getDeviceInfo = () => {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timestamp: new Date().toISOString()
  };
};

/**
 * Validate FCM token format
 * @param {string} token - FCM token to validate
 * @returns {boolean} True if token format is valid
 */
export const isValidFCMToken = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }
  
  // FCM tokens are typically base64 encoded strings with specific patterns
  // This is a basic validation - you might want to adjust based on your needs
  const fcmTokenPattern = /^[A-Za-z0-9_-]+:[A-Za-z0-9_-]+$/;
  return fcmTokenPattern.test(token) && token.length > 50;
};

/**
 * Format notification payload for display
 * @param {Object} payload - Firebase message payload
 * @returns {Object} Formatted notification data
 */
export const formatNotificationPayload = (payload) => {
  return {
    title: payload.notification?.title || payload.data?.title || 'New Notification',
    body: payload.notification?.body || payload.data?.body || 'You have a new message',
    icon: payload.notification?.icon || payload.data?.icon || '/logo192.png',
    image: payload.notification?.image || payload.data?.image,
    clickAction: payload.notification?.click_action || payload.data?.click_action,
    data: payload.data || {},
    timestamp: new Date().toISOString()
  };
};

/**
 * Create notification options for browser notifications
 * @param {Object} payload - Firebase message payload
 * @returns {Object} Notification options
 */
export const createNotificationOptions = (payload) => {
  const formatted = formatNotificationPayload(payload);
  
  return {
    body: formatted.body,
    icon: formatted.icon || '/appicon.png',
    badge: '/appicon.png',
    tag: 'complimate-notification',
    requireInteraction: true,
    silent: false,
    timestamp: Date.now(),
    actions: [
      {
        action: 'open',
        title: 'Open App',
        icon: '/appicon.png'
      },
      {
        action: 'close',
        title: 'Close'
      }
    ],
    data: formatted.data
  };
};

/**
 * Show browser notification
 * @param {string} title - Notification title
 * @param {Object} options - Notification options
 * @returns {Promise<Notification>} Notification instance
 */
export const showBrowserNotification = async (title, options = {}) => {
  if (!('Notification' in window)) {
    throw new Error('This browser does not support notifications');
  }

  if (Notification.permission !== 'granted') {
    throw new Error('Notification permission not granted');
  }

  return new Notification(title, options);
};

/**
 * Check if service worker is supported
 * @returns {boolean} True if service worker is supported
 */
export const isServiceWorkerSupported = () => {
  return 'serviceWorker' in navigator;
};

/**
 * Register service worker
 * @param {string} swPath - Path to service worker file
 * @returns {Promise<ServiceWorkerRegistration>} Service worker registration
 */
export const registerServiceWorker = async (swPath = '/firebase-messaging-sw.js') => {
  if (!isServiceWorkerSupported()) {
    throw new Error('Service worker not supported in this browser');
  }

  try {
    const registration = await navigator.serviceWorker.register(swPath);
    console.log('Service worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('Service worker registration failed:', error);
    throw error;
  }
};

/**
 * Get service worker registration
 * @returns {Promise<ServiceWorkerRegistration|null>} Service worker registration or null
 */
export const getServiceWorkerRegistration = async () => {
  if (!isServiceWorkerSupported()) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    return registration;
  } catch (error) {
    console.error('Error getting service worker registration:', error);
    return null;
  }
};

/**
 * Storage utilities for FCM token
 */
export const FCMStorage = {
  // Save token to localStorage
  saveToken: (token) => {
    try {
      localStorage.setItem('fcm_token', token);
      localStorage.setItem('fcm_token_timestamp', Date.now().toString());
    } catch (error) {
      console.error('Error saving FCM token to localStorage:', error);
    }
  },

  // Get token from localStorage
  getToken: () => {
    try {
      return localStorage.getItem('fcm_token');
    } catch (error) {
      console.error('Error getting FCM token from localStorage:', error);
      return null;
    }
  },

  // Remove token from localStorage
  removeToken: () => {
    try {
      localStorage.removeItem('fcm_token');
      localStorage.removeItem('fcm_token_timestamp');
    } catch (error) {
      console.error('Error removing FCM token from localStorage:', error);
    }
  },

  // Check if token exists and is recent (within 24 hours)
  isTokenRecent: () => {
    try {
      const timestamp = localStorage.getItem('fcm_token_timestamp');
      if (!timestamp) return false;
      
      const tokenAge = Date.now() - parseInt(timestamp);
      const twentyFourHours = 24 * 60 * 60 * 1000;
      
      return tokenAge < twentyFourHours;
    } catch (error) {
      console.error('Error checking token age:', error);
      return false;
    }
  }
};

/**
 * Error handling utilities
 */
export const FirebaseErrorHandler = {
  // Handle FCM token errors
  handleTokenError: (error) => {
    console.error('FCM Token Error:', error);
    
    // You can add custom error handling logic here
    // For example, retry logic, user notification, etc.
    
    return {
      type: 'TOKEN_ERROR',
      message: error.message,
      timestamp: new Date().toISOString()
    };
  },

  // Handle notification permission errors
  handlePermissionError: (error) => {
    console.error('Notification Permission Error:', error);
    
    return {
      type: 'PERMISSION_ERROR',
      message: error.message,
      timestamp: new Date().toISOString()
    };
  },

  // Handle service worker errors
  handleServiceWorkerError: (error) => {
    console.error('Service Worker Error:', error);
    
    return {
      type: 'SERVICE_WORKER_ERROR',
      message: error.message,
      timestamp: new Date().toISOString()
    };
  }
};
